import React, { useMemo, useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  BottomNavigation,
  BottomNavigationAction,
  Card,
  CardContent,
  Chip,
  Fab,
  Badge,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CalendarToday,
  Home,
  Person,
  RequestQuote,
  Add,
  Notifications,
  ChevronRight,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import Logo from '../components/Logo';
import { useAuth } from '../context/AuthContext';

const GET_MY_SHIFTS = gql`
  query MobileMyShifts($employeeId: String, $startDate: String!, $endDate: String!) {
    myShifts(employeeId: $employeeId, startDate: $startDate, endDate: $endDate) {
      id
      startDate
      endDate
      shiftType
      department
      position
      status
      hours
    }
  }
`;

const toIso = (date: Date) => date.toISOString();

const getRange = () => {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 14);
  return { start: toIso(start), end: toIso(end) };
};

const labelDay = (isoDate: string) => {
  const date = new Date(isoDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);

  if (target.getTime() === today.getTime()) return 'Today';
  if (target.getTime() === tomorrow.getTime()) return 'Tomorrow';
  return target.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatTimeWindow = (startIso: string, endIso: string) => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const format = (d: Date) =>
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${format(start)} - ${format(end)}`;
};

const MySchedule: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [value, setValue] = useState(1);

  const range = useMemo(() => getRange(), []);
  const { data, loading, error } = useQuery(GET_MY_SHIFTS, {
    variables: {
      employeeId: user?.id,
      startDate: range.start,
      endDate: range.end,
    },
    skip: !user?.id,
    fetchPolicy: 'cache-and-network',
  });

  const shifts = (data?.myShifts ?? []).map((shift: any) => ({
    id: shift.id,
    dateLabel: labelDay(shift.startDate),
    time: formatTimeWindow(shift.startDate, shift.endDate),
    type: (shift.shiftType || 'CUSTOM').toString(),
    department: shift.department || 'General',
    status: (shift.status || 'PENDING').toString(),
    hours: Number(shift.hours || 0),
  }));

  const totalHours = shifts.reduce((sum: number, s: any) => sum + s.hours, 0);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5', pb: 8 }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#1a1a2e',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Toolbar>
          <Logo size={36} />
          <Typography variant="h6" sx={{ ml: 2, fontWeight: 700, flexGrow: 1 }}>
            My Schedule
          </Typography>
          <IconButton color="inherit">
            <Badge badgeContent={0} color="error">
              <Notifications />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a2e', mb: 0.5 }}>
            Hello, {user?.name || 'Staff'}!
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            You have {shifts.length} upcoming shifts
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 2,
            mb: 3,
          }}
        >
          {[
            { label: 'Hours', value: `${Math.round(totalHours)}`, color: '#E91E63' },
            { label: 'Shifts', value: `${shifts.length}`, color: '#9C27B0' },
            { label: 'Status', value: shifts.some((s: any) => s.status === 'PENDING') ? 'Pending' : 'Ready', color: '#4CAF50' },
          ].map((stat, i) => (
            <Card key={i} sx={{ borderRadius: 3, textAlign: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: stat.color, mb: 0.5 }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e' }}>
          Upcoming Shifts
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && <Alert severity="error">Could not load your schedule.</Alert>}

        {!loading && !error && shifts.length === 0 && (
          <Alert severity="info">No shifts scheduled in the selected period.</Alert>
        )}

        {!loading &&
          !error &&
          shifts.map((shift: any) => (
            <Card
              key={shift.id}
              sx={{
                mb: 2,
                borderRadius: 4,
                borderLeft: `4px solid ${shift.status === 'CONFIRMED' ? '#4CAF50' : '#FF9800'}`,
                boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                transition: 'transform 0.2s ease',
                '&:active': { transform: 'scale(0.98)' },
              }}
              onClick={() => navigate(`/shift/${shift.id}`)}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {shift.dateLabel}
                      </Typography>
                      <Chip
                        label={shift.status.toLowerCase()}
                        size="small"
                        sx={{
                          bgcolor:
                            shift.status === 'CONFIRMED'
                              ? 'rgba(76, 175, 80, 0.1)'
                              : 'rgba(255, 152, 0, 0.1)',
                          color: shift.status === 'CONFIRMED' ? '#4CAF50' : '#FF9800',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      {shift.time}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: 'rgba(233, 30, 99, 0.1)',
                          color: '#E91E63',
                          fontSize: '0.75rem',
                        }}
                      >
                        {shift.department.charAt(0)}
                      </Avatar>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        {shift.department}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Chip
                      label={shift.type}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: shift.type === 'NIGHT' ? '#9C27B0' : '#E91E63',
                        color: shift.type === 'NIGHT' ? '#9C27B0' : '#E91E63',
                        fontWeight: 600,
                        mb: 1,
                      }}
                    />
                    <ChevronRight sx={{ color: '#999' }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
      </Box>

      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 90,
          right: 16,
          bgcolor: '#E91E63',
          width: 60,
          height: 60,
          boxShadow: '0 8px 30px rgba(233, 30, 99, 0.4)',
          '&:active': { transform: 'scale(0.9)' },
        }}
        onClick={() => navigate('/leave')}
      >
        <Add />
      </Fab>

      <BottomNavigation
        value={value}
        onChange={(_event, newValue) => setValue(newValue)}
        sx={{
          position: 'fixed',
          bottom: 0,
          width: '100%',
          bgcolor: 'white',
          borderTop: '1px solid #e0e0e0',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.05)',
          pb: 'env(safe-area-inset-bottom)',
        }}
      >
        <BottomNavigationAction
          icon={<Home />}
          label="Home"
          sx={{ color: value === 0 ? '#E91E63' : '#999' }}
          onClick={() => navigate('/schedule')}
        />
        <BottomNavigationAction icon={<CalendarToday />} label="Schedule" sx={{ color: value === 1 ? '#E91E63' : '#999' }} />
        <BottomNavigationAction
          icon={<RequestQuote />}
          label="Leave"
          sx={{ color: value === 2 ? '#E91E63' : '#999' }}
          onClick={() => navigate('/leave')}
        />
        <BottomNavigationAction
          icon={<Person />}
          label="Profile"
          sx={{ color: value === 3 ? '#E91E63' : '#999' }}
          onClick={() => navigate('/profile')}
        />
      </BottomNavigation>
    </Box>
  );
};

export default MySchedule;
